import React, { useEffect, useState } from "react";
import { CheckCircle2, Circle, Loader2, Calendar, X } from "lucide-react";
import { api } from "@/lib/api";
import { TIMELINE } from "@/constants/testIds";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function WeaverTimeline({ product, onUpdated }) {
  const [timeline, setTimeline] = useState(null);
  const [starting, setStarting] = useState(false);
  const [dateInput, setDateInput] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!product?.id) return;
    setLoading(true);
    api
      .get(`/products/${product.id}/timeline`)
      .then(({ data }) => setTimeline(data))
      .catch(() => setTimeline({ started: false }))
      .finally(() => setLoading(false));
  }, [product?.id, product?.timeline_start]);

  if (!product || product.availability !== "Made-to-order") return null;

  const start = async () => {
    setStarting(true);
    try {
      const body = dateInput ? { start_date: dateInput } : {};
      const { data } = await api.patch(
        `/products/${product.id}/timeline`,
        body,
      );
      setTimeline({ started: true, ...data.timeline });
      setDateInput("");
      toast.success("Timeline started");
      onUpdated?.();
    } catch {
      toast.error("Failed to start timeline");
    } finally {
      setStarting(false);
    }
  };

  const clear = async () => {
    try {
      await api.patch(`/products/${product.id}/timeline`, { clear: true });
      setTimeline({ started: false });
      toast("Timeline cleared");
      onUpdated?.();
    } catch {
      toast.error("Failed");
    }
  };

  if (loading) {
    return <div className="text-xs text-stone-400 mt-2">Loading timeline…</div>;
  }

  if (!timeline?.started) {
    return (
      <div
        data-testid={TIMELINE.card(product.id)}
        className="mt-3 rounded-xl border border-dashed border-stone-300 p-2.5 flex items-center gap-2 text-xs"
      >
        <Calendar className="w-3.5 h-3.5 text-stone-400 shrink-0" />
        <span className="text-stone-500">Weaver timeline</span>
        <Input
          type="date"
          value={dateInput}
          onChange={(e) => setDateInput(e.target.value)}
          className="h-7 text-xs w-32"
        />
        <Button
          data-testid={TIMELINE.startButton(product.id)}
          size="sm"
          variant="ghost"
          disabled={starting}
          onClick={start}
          className="h-7 text-xs text-terracotta hover:bg-terracotta/10 ml-auto"
        >
          {starting ? "Starting…" : "Start"}
        </Button>
      </div>
    );
  }

  const daysLeft = Math.max(
    Math.ceil(timeline.total_days - timeline.days_elapsed),
    0,
  );

  return (
    <div
      data-testid={TIMELINE.card(product.id)}
      className="mt-3 rounded-xl border border-stone-200 p-3 bg-stone-50/50"
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline text-terracotta">Weaver timeline</div>
          <div className="text-xs text-stone-600 mt-0.5">
            {timeline.percent}% · {daysLeft > 0 ? `~${daysLeft} days left` : "Ready!"}
          </div>
        </div>
        <button
          data-testid={TIMELINE.clearButton(product.id)}
          onClick={clear}
          className="text-stone-400 hover:text-terracotta"
          title="Clear timeline"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div
        data-testid={TIMELINE.bar(product.id)}
        className="h-1.5 rounded-full bg-stone-200 overflow-hidden mb-2.5"
      >
        <div
          className="h-full bg-terracotta transition-all duration-500"
          style={{ width: `${timeline.percent}%` }}
        />
      </div>
      <div className="space-y-1">
        {timeline.stages?.map((s) => (
          <div key={s.name} className="flex items-center gap-2 text-xs">
            {s.status === "done" ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            ) : s.status === "active" ? (
              <Loader2 className="w-3.5 h-3.5 text-terracotta animate-spin" />
            ) : (
              <Circle className="w-3.5 h-3.5 text-stone-300" />
            )}
            <span
              className={
                s.status === "done"
                  ? "text-stone-500 line-through"
                  : s.status === "active"
                    ? "text-stone-800 font-medium"
                    : "text-stone-400"
              }
            >
              {s.name}
            </span>
            <span className="ml-auto text-stone-400 text-[10px]">
              {s.days}d
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
